import React, { useState, useRef, useEffect } from 'react';

import Dropdown from './Dropdown';
import DateInput from './DateInput';
import { A, modalInputStyle as iS, btnCancel as bCancel, btnSave as bSave } from './theme';
const TYPES = ['exam','class','assignment','lab','meeting','deadline','reminder','event','other'];
const DAYS = ['S','M','T','W','T','F','S'];

function TaskForm({ api, task, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task?.title||'', type: task?.type||'other', date: task?.date||'',
    time: task?.time?.slice(0,5)||'', recurrence: task?.recurrence||'',
    recurrenceDays: task?.recurrenceDays||[], description: task?.description||'', interval: task?.interval||1,
    exceptDays: task?.exceptDays||null, exceptDates: task?.exceptDates||null, until: task?.until||'',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  // Stable ref for onClose to avoid re-registering focus trap
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus trap
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const focusable = () => el.querySelectorAll('input,textarea,button,select,[tabindex]:not([tabindex="-1"])');
    const items = focusable();
    if (items.length) items[0].focus();
    const handleKeyDown = e => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const els = focusable();
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, []); // eslint-disable-line
  const toggleDay = d => setForm(p=>({...p,recurrenceDays:p.recurrenceDays.includes(d)?p.recurrenceDays.filter(x=>x!==d):[...p.recurrenceDays,d]}));

  const save = async () => {
    if (!form.title.trim()) { setError('Mission name required'); return; }
    setSaving(true); setError('');
    try {
      const body = { title:form.title.trim(), type:form.type, date:form.date||null, time:form.time||null,
        recurrence:form.recurrence||null, recurrenceDays:form.recurrenceDays.length?form.recurrenceDays:null,
        description:form.description.trim()||null, interval:form.interval,
        exceptDays:form.exceptDays, exceptDates:form.exceptDates, until:form.until||null };
      const url = task ? `${api}/dashboard/tasks/${task.id}` : `${api}/dashboard/tasks`;
      const res = await fetch(url, { method:task?'PUT':'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); setError(d.error||'Failed'); setSaving(false); return; }
      onSaved();
    } catch(e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',display:'flex',justifyContent:'center',alignItems:'center',zIndex:1000 }} onClick={onClose} onKeyDown={e=>e.key==='Escape'&&onClose()}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="task-form-title" style={{
        background:A.surfaceModal, borderRadius:'16px', padding:'24px', width:'420px', maxWidth:'92vw',
        border:`1px solid ${A.border}`, maxHeight:'85vh', overflowY:'auto',
        boxShadow:`0 0 40px rgba(123,104,238,0.1), 0 0 80px rgba(123,104,238,0.05)`,
        position:'relative', animation:'fsu 0.3s ease-out',
      }} onClick={e=>e.stopPropagation()}>
        {/* Top glow */}
        <div style={{ position:'absolute',top:0,left:'15%',right:'15%',height:'1px',background:`linear-gradient(90deg,transparent,${A.primary},${A.accent},transparent)` }} />

        <div style={{ display:'flex',alignItems:'center',gap:'8px',marginBottom:'18px' }}>
          <div style={{ width:'8px',height:'8px',borderRadius:'50%',background:A.primary,boxShadow:`0 0 8px ${A.primary}` }} />
          <h3 id="task-form-title" style={{ margin:0,color:A.text,fontSize:'14px',fontWeight:700,letterSpacing:'2px',textTransform:'uppercase' }}>
            {task?'Edit Mission':'New Mission'}
          </h3>
        </div>

        <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
          <input value={form.title} onChange={e=>set('title',e.target.value)} placeholder="Mission name *" aria-label="Mission name" style={iS} autoFocus />
          <Dropdown value={form.type} onChange={v=>set('type',v)} aria-label="Task type" options={TYPES.map(t=>({value:t,label:t}))} />
          {form.recurrence && (
            <div style={{padding:'8px 10px',borderRadius:'6px',background:`${A.accent}08`,border:`1px dashed ${A.accent}30`}}>
              <span style={{fontSize:'10px',color:A.accent,letterSpacing:'0.5px',fontWeight:600,display:'block',marginBottom:'6px'}}>
                {form.date ? '📅 STARTS FROM' : '📅 SET START DATE'}
              </span>
              <input type="date" value={form.date||''} onChange={e=>set('date',e.target.value)}
                aria-label="Recurrence start date"
                style={{...iS,width:'100%',borderColor:`${A.accent}40`,colorScheme:'dark'}} />
            </div>
          )}
          {!form.recurrence && (
            <DateInput value={form.date} onChange={e=>set('date',e.target.value)}
              placeholder="Date"
              aria-label="Task date"
              style={{...iS,flex:1}} />
          )}
          <input type="time" value={form.time} onChange={e=>set('time',e.target.value)} aria-label="Task time" style={iS} />
          <Dropdown value={form.recurrence} onChange={v=>set('recurrence',v)} aria-label="Recurrence pattern"
            options={[{value:'',label:'One-time'},{value:'daily',label:'Daily'},{value:'weekly',label:'Weekly'},{value:'custom',label:'Custom'}]} />
          {(form.recurrence==='weekly'||form.recurrence==='custom')&&(
            <div style={{ display:'flex',gap:'3px' }}>
              {DAYS.map((d,i)=>(
                <button key={i} onClick={()=>toggleDay(i)} style={{
                  width:'36px',height:'32px',borderRadius:'6px',border:'none',cursor:'pointer',
                  background:form.recurrenceDays.includes(i)?`${A.primary}30`:'rgba(255,255,255,0.03)',
                  color:form.recurrenceDays.includes(i)?A.primary:A.dim,
                  fontSize:'11px',fontWeight:700, transition:'background 0.15s, color 0.15s, border-color 0.15s',
                  border:form.recurrenceDays.includes(i)?`1px solid ${A.primary}40`:'1px solid transparent',
                }}>{d}</button>
              ))}
            </div>
          )}
          {form.recurrence&&(
            <>
              <div style={{ display:'flex',alignItems:'center',gap:'6px' }}>
                <span style={{ color:A.dim,fontSize:'11px',letterSpacing:'1px' }}>EVERY</span>
                <input type="number" min="1" max="4" value={form.interval} onChange={e=>set('interval',parseInt(e.target.value)||1)} style={{...iS,width:'50px',textAlign:'center'}} aria-label="Repeat interval" />
                <span style={{ color:A.dim,fontSize:'11px' }}>{form.recurrence==='daily'?'DAY(S)':'WEEK(S)'}</span>
              </div>
              <div style={{ display:'flex',alignItems:'center',gap:'6px' }}>
                <span style={{ color:A.dim,fontSize:'11px',letterSpacing:'1px' }}>UNTIL</span>
                <DateInput value={form.until} onChange={e=>set('until',e.target.value)} placeholder="End date (optional)" aria-label="Recurrence end date" style={{...iS,flex:1}} />
              </div>
            </>
          )}
          <textarea value={form.description} onChange={e=>set('description',e.target.value)} placeholder="Mission notes (optional)" aria-label="Mission notes" rows={2} style={{...iS,resize:'none',fontFamily:'inherit'}} />
        </div>

        {error&&<p role="alert" style={{ color:'#ef4444',fontSize:'11px',margin:'8px 0 0',letterSpacing:'0.5px' }}>{error}</p>}

        <div style={{ display:'flex',gap:'6px',marginTop:'16px',justifyContent:'flex-end' }}>
          <button onClick={onClose} style={bCancel}>ABORT</button>
          <button onClick={save} disabled={saving} style={bSave}>{saving?'DEPLOYING...':'DEPLOY'}</button>
        </div>
      </div>
    </div>
  );
}

/* Styles imported from theme.js */

export default TaskForm;
